import React, { useState, useEffect } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus, Search } from 'lucide-react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import TaskCard from '../components/TaskCard';
import TaskDetailModal from '../components/TaskDetailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusLabels = {
  open: 'Open',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  completed: 'Completed',
};

const statusColors = {
  open: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  blocked: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
};

const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/tasks`);
      setTasks(res.data);
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = () => {
    // Visual reorder only — order is not persisted to backend
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  };

  const handleTaskUpdate = async (updatedTask) => {
    try {
      const payload = {
        title: updatedTask.title,
        description: updatedTask.description,
        status: updatedTask.status,
        priority: updatedTask.priority,
        assignee: updatedTask.assignee,
        assignee_initials: updatedTask.assignee_initials,
        department: updatedTask.department,
        due_date: updatedTask.due_date || null,
        tags: updatedTask.tags || [],
      };

      // New (unsaved) task → POST; existing task → PUT
      if (updatedTask._isNew) {
        const res = await axios.post(`${API_URL}/api/tasks`, payload);
        setTasks((prev) => [...prev, res.data]);
      } else {
        const res = await axios.put(`${API_URL}/api/tasks/${updatedTask.id}`, payload);
        setTasks((prev) => prev.map((t) => (t.id === res.data.id ? res.data : t)));
      }

      setIsModalOpen(false);
      setSelectedTask(null);
    } catch (err) {
      console.error('Error saving task:', err);
      alert('Failed to save task. Please try again.');
    }
  };

  const handleTaskDelete = async (taskId) => {
    try {
      // Unsaved new task — just close modal, nothing to delete in DB
      if (selectedTask?._isNew) {
        setIsModalOpen(false);
        setSelectedTask(null);
        return;
      }
      await axios.delete(`${API_URL}/api/tasks/${taskId}`);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      setIsModalOpen(false);
      setSelectedTask(null);
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Failed to delete task. Please try again.');
    }
  };

  const handleAddTask = () => {
    // Open empty modal — task is only persisted when user clicks Save
    const draft = {
      _isNew: true,
      id: 'new',
      title: '',
      description: '',
      status: 'open',
      priority: 'medium',
      assignee: 'Brittany Chandler',
      assignee_initials: 'BC',
      department: '3.1 Program',
      due_date: null,
      tags: [],
    };
    setSelectedTask(draft);
    setIsModalOpen(true);
  };

  // Filter tasks
  const filteredTasks = tasks.filter((task) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      task.title.toLowerCase().includes(q) ||
      (task.description || '').toLowerCase().includes(q);
    const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    return matchesSearch && matchesPriority && matchesStatus;
  });

  // Group by status
  const groupedTasks = {
    open: filteredTasks.filter((t) => t.status === 'open'),
    in_progress: filteredTasks.filter((t) => t.status === 'in_progress'),
    blocked: filteredTasks.filter((t) => t.status === 'blocked'),
    completed: filteredTasks.filter((t) => t.status === 'completed'),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="tasks-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tasks</h1>
          <p className="text-sm text-muted-foreground mt-1">
            3.1 Program Operations & Compliance
          </p>
        </div>
        <Button onClick={handleAddTask} data-testid="tasks-new-button">
          <Plus className="h-4 w-4 mr-2" />
          New Task
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="tasks-search-input"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-3 py-2 border rounded-md text-sm bg-background"
              data-testid="tasks-priority-filter"
            >
              <option value="all">All Priorities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border rounded-md text-sm bg-background"
              data-testid="tasks-status-filter"
            >
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="blocked">Blocked</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Task Stats */}
      <div className="grid grid-cols-4 gap-4">
        {['open', 'in_progress', 'blocked', 'completed'].map((s) => (
          <Card className="p-4" key={s}>
            <div className="text-sm text-muted-foreground mb-1">{statusLabels[s]}</div>
            <div className="text-2xl font-bold" data-testid={`tasks-count-${s}`}>
              {groupedTasks[s].length}
            </div>
          </Card>
        ))}
      </div>

      {/* Kanban Board - 4 Equal Columns */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {Object.entries(groupedTasks).map(([status, statusTasks]) => (
          <div key={status} className="flex-1 min-w-[280px] flex flex-col gap-3">
            <div className="flex items-center justify-between bg-background pb-3 border-b">
              <Badge className={statusColors[status]}>
                {statusLabels[status]}
              </Badge>
              <span className="text-sm text-muted-foreground">{statusTasks.length}</span>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={statusTasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3 min-h-[500px]" data-testid={`tasks-column-${status}`}>
                  {statusTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onClick={() => handleTaskClick(task)}
                    />
                  ))}
                  {statusTasks.length === 0 && (
                    <div className="text-center py-12 text-sm text-muted-foreground">
                      No tasks
                    </div>
                  )}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        ))}
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onUpdate={handleTaskUpdate}
          onDelete={handleTaskDelete}
        />
      )}
    </div>
  );
};

export default Tasks;
